import { useEffect, useRef, useState } from "react";

/**
 * Normalized scroll progress (0–1) for how far `ref`'s element has moved through the viewport.
 *
 * @param {number} [finishAt=1] Raw progress (0–1) at which output reaches 1. Lower = full reveal finishes earlier in the scroll (e.g. 0.82).
 */
export function useScrollProgress(ref, finishAt = 1) {
  const finishDenom = finishAt > 0 && finishAt <= 1 ? finishAt : 1;
  const [progress, setProgress] = useState(() => {
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return 1;
    }
    return 0;
  });
  const rafRef = useRef(0);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq.matches) {
      return undefined;
    }

    const compute = () => {
      const el = ref.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = vh + rect.height;
      if (total <= 0) return;

      const scrolled = vh - rect.top;
      const raw = Math.min(1, Math.max(0, scrolled / total));
      const p = Math.min(1, raw / finishDenom);
      setProgress(p);
    };

    const schedule = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        compute();
      });
    };

    compute();
    schedule();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);

    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [ref, finishDenom]);

  return progress;
}
